import { concat } from '@ember/helper';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, not } from 'ember-truth-helpers';
import GeneralInputsInputWithKeyboardHandling from 'lumen/components/general/inputs/input-with-keyboard-handling';

interface GeneralTablesCellsTdEditableSignature {
  Args: {
    column: {
      maxLength?: number;
      name: string;
    };
    displayValue: {
      onEdit: (newValue?: string) => void;
      value: string;
    };
    row: {
      isDisabled?: boolean;
      isReadOnly?: boolean;
    };
    value?: string;
  };
}

export default class GeneralTablesCellsTdEditable extends Component<GeneralTablesCellsTdEditableSignature> {
  @tracked isEditing = false;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDisabled() {
    return this.args.row.isDisabled;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isReadOnly() {
    return this.args.row.isReadOnly;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get valueOrDefault() {
    return this.args.value ?? '';
  }

  @action discardValue(): void {
    // Check to avoid updating twice in 1 computation cycle
    if (!this.isEditing) {
      return;
    }

    this.isEditing = false;
  }

  @action onClickEdit(): void {
    this.isEditing = true;
  }

  @action setValue(value: string): void {
    this.args.displayValue.onEdit(value);

    // Check to avoid updating twice in 1 computation cycle
    if (!this.isEditing) {
      return;
    }
    this.isEditing = false;
  }

  <template>
    <div class="td-editable flex w-full" data-test-id="td-editable">
      {{#if this.isEditing}}
        <GeneralInputsInputWithKeyboardHandling
          @autofocus={{true}}
          @isSmall={{true}}
          @maxLength={{@column.maxLength}}
          @onDiscard={{this.discardValue}}
          @onEdit={{this.setValue}}
          @value={{this.valueOrDefault}}
          data-test-id="td-editable-input"
        />
      {{else}}
        <div
          class="td-editable__value-container flex flex-1 items-center justify-between overflow-hidden"
        >
          <span
            class="flex-1 overflow-hidden text-ellipsis whitespace-nowrap
              {{if this.isDisabled 'text-disabled'}}"
            title={{@displayValue.value}}
          >
            {{@displayValue.value}}
          </span>

          {{#if (and (not this.isReadOnly) (not this.isDisabled))}}
            <div class="td-editable__button-container">
              <PrismButton
                @buttonType="ghost"
                @icon="edit-pencil"
                @onClick={{this.onClickEdit}}
                @size="small"
                aria-label={{concat
                  (t "general.edit")
                  " "
                  @column.name
                  " "
                  @displayValue.value
                }}
                class="td-editable__button"
                data-test-id="td-editable-button-edit"
              />
            </div>
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}
